import { CubeContext } from '@cubejs-client/react';
import { useCallback, useContext } from 'react';
import { useGetUserCountries } from '@/api/queries/country/useGetUserCountries';
import { useAuthContext } from '@/context/AuthContext';
import FiltersContext, { OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import { getDefaultFiltersState, rehydrateFilterState } from '@/context/FiltersContext/FiltersContextProvider';
import { globalFilters } from '@/models/filter/globalFilterAllowlist';
import type { FilterState } from '@/types/filter/filterState';
import type { SelectItem } from '@/types/ui/selectItem';
import { reconstructMissingOptionPaths } from '@/utils/filter/reconstructOptionPaths';

export type ReconstructOptionPaths = (
  filters: FilterState,
  optionPaths?: OptionPathsByFilter,
) => Promise<OptionPathsByFilter | undefined>;

export const useReconstructOptionPaths = (): ReconstructOptionPaths => {
  const { cubejsApi } = useContext(CubeContext);
  const { userInfo } = useAuthContext();
  const { filterConfig } = useContext(FiltersContext);
  const { data: userCountries } = useGetUserCountries<SelectItem<string>[]>();

  return useCallback(
    async (filters, optionPaths) => {
      const candidateState = rehydrateFilterState(filters, getDefaultFiltersState(filterConfig ?? globalFilters));
      try {
        return await reconstructMissingOptionPaths({ cubejsApi, userInfo, userCountries, candidateState, optionPaths });
      } catch {
        // Best-effort: the recipient re-runs reconstruction on apply, so a failed pass must not block sharing
        return optionPaths;
      }
    },
    [cubejsApi, userInfo, filterConfig, userCountries],
  );
};
